import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, User, XCircle } from 'lucide-react'

import { fetchVotesByProposal } from '@/api'
import { queryKeys } from '@/queries/keys'
import UiAddressCopy from '@/ui/UiAddressCopy'
import { UiBadge } from '@/ui/UiBadge'
import {
  UiCard,
  UiCardContent,
  UiCardDescription,
  UiCardHeader,
  UiCardTitle,
} from '@/ui/UiCard'
import { UiSkeleton } from '@/ui/UiSkeleton'

type Props = {
  proposalId: string
  threshold?: string
  totalMembers?: number
}

export default function VotesBreakdown({
  proposalId,
  threshold,
  totalMembers,
}: Props) {
  const votesQuery = useQuery({
    queryKey: queryKeys.multisig.proposalVotes(proposalId),
    enabled: Boolean(proposalId),
    queryFn: () => fetchVotesByProposal(proposalId),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  if (votesQuery.isPending) {
    return (
      <UiCard>
        <UiCardHeader>
          <UiCardTitle>Votes</UiCardTitle>
          <UiCardDescription>Loading votes...</UiCardDescription>
        </UiCardHeader>
        <UiCardContent>
          <div className='space-y-3'>
            <UiSkeleton className='h-4 w-full' />
            <UiSkeleton className='h-4 w-3/4' />
            <UiSkeleton className='h-4 w-2/3' />
          </div>
        </UiCardContent>
      </UiCard>
    )
  }

  if (votesQuery.isError) {
    return (
      <UiCard>
        <UiCardHeader>
          <UiCardTitle>Votes</UiCardTitle>
          <UiCardDescription>Failed to load votes</UiCardDescription>
        </UiCardHeader>
        <UiCardContent>
          <p className='text-muted-foreground text-sm'>
            {votesQuery.error instanceof Error
              ? votesQuery.error.message
              : 'An error occurred'}
          </p>
        </UiCardContent>
      </UiCard>
    )
  }

  const votes = votesQuery.data?.vote ?? []
  const yesVotes = votes.filter(v => v.option === 'YES')
  const noVotes = votes.filter(v => v.option === 'NO')
  const totalVotes = votes.length

  return (
    <UiCard>
      <UiCardHeader>
        <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
          <div className='space-y-1.5'>
            <UiCardTitle>Votes</UiCardTitle>
            <UiCardDescription>
              {totalMembers
                ? `${totalVotes} of ${totalMembers} members voted`
                : `${totalVotes} vote${totalVotes !== 1 ? 's' : ''} submitted`}
              {threshold && ` • Threshold: ${threshold}`}
            </UiCardDescription>
          </div>
          <div className='flex flex-wrap gap-2'>
            <UiBadge
              variant='outline'
              className='border-green-500/50 bg-green-500/10 text-green-600 dark:text-green-400'
            >
              <CheckCircle2 className='mr-1 h-3 w-3' />
              YES: {yesVotes.length}
            </UiBadge>
            <UiBadge
              variant='outline'
              className='border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400'
            >
              <XCircle className='mr-1 h-3 w-3' />
              NO: {noVotes.length}
            </UiBadge>
          </div>
        </div>
      </UiCardHeader>

      <UiCardContent>
        {votes.length === 0 ? (
          <div className='py-8 text-center'>
            <User className='text-muted-foreground mx-auto mb-3 h-12 w-12 opacity-40' />
            <p className='text-muted-foreground text-sm font-medium'>
              No votes yet
            </p>
            <p className='text-muted-foreground mt-1 text-xs'>
              Waiting for multisig members to vote
            </p>
          </div>
        ) : (
          <div className='space-y-4'>
            {/* YES Votes Section */}
            {yesVotes.length > 0 && (
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <CheckCircle2 className='h-4 w-4 text-green-600 dark:text-green-400' />
                  <h3 className='text-foreground text-sm font-semibold'>
                    Approved ({yesVotes.length})
                  </h3>
                </div>
                <div className='space-y-2'>
                  {yesVotes.map((vote, idx) => (
                    <div
                      key={`yes-${vote.voter}-${idx}`}
                      className='bg-muted/50 flex flex-col gap-2 rounded-lg p-3 sm:flex-row sm:items-center sm:justify-between'
                    >
                      <div className='min-w-0 flex-1'>
                        <UiAddressCopy
                          start={20}
                          end={10}
                          address={vote.voter}
                          className='font-mono text-xs'
                        />
                      </div>
                      <div className='text-muted-foreground flex items-center gap-2 text-xs'>
                        <span className='hidden sm:inline'>Block:</span>
                        <span className='font-mono'>{vote.submitBlock}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* NO Votes Section */}
            {noVotes.length > 0 && (
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <XCircle className='h-4 w-4 text-red-600 dark:text-red-400' />
                  <h3 className='text-foreground text-sm font-semibold'>
                    Rejected ({noVotes.length})
                  </h3>
                </div>
                <div className='space-y-2'>
                  {noVotes.map((vote, idx) => (
                    <div
                      key={`no-${vote.voter}-${idx}`}
                      className='bg-muted/50 flex flex-col gap-2 rounded-lg p-3 sm:flex-row sm:items-center sm:justify-between'
                    >
                      <div className='min-w-0 flex-1'>
                        <UiAddressCopy
                          start={20}
                          end={10}
                          address={vote.voter}
                          className='font-mono text-xs'
                        />
                      </div>
                      <div className='text-muted-foreground flex items-center gap-2 text-xs'>
                        <span className='hidden sm:inline'>Block:</span>
                        <span className='font-mono'>{vote.submitBlock}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </UiCardContent>
    </UiCard>
  )
}
